import React, { useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Divider,
    Paper,
    Box,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { format, parseISO } from "date-fns";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MMMM d, yyyy");
    } catch {
        return dateStr;
    }
}

function getScoreChipStyle(score, targetAttenuation = 100) {
    const n = parseFloat(score);
    const target = parseFloat(targetAttenuation);
    if (isNaN(n) || isNaN(target)) return { color: "default" };
    if (n >= target) return { color: "success" }; // A
    if (n >= target - 10) return { color: "info" }; // B
    if (n >= target - 20) return { color: "warning" }; // C
    if (n >= target - 30) return { color: "error" }; // D
    return {
        color: "default", // F
        sx: {
            bgcolor: "#6d0000",
            color: "#fff",
            "& .MuiChip-label": { color: "#fff" },
        },
    };
}

function formatGEMagnetDisplay(magnetType, magnetStrength) {
    if (magnetType !== "GE" || !magnetStrength) {
        return magnetType || "—";
    }
    const strength = String(magnetStrength).trim();
    return `GE ${/t$/i.test(strength) ? strength : `${strength}T`}`;
}

function InfoRow({ label, value }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 0.5,
                columnGap: 1,
            }}
        >
            <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ minWidth: 120, flexShrink: 0 }}
            >
                {label}:
            </Typography>
            <Typography variant="body2">{value || "—"}</Typography>
        </Box>
    );
}

function SectionPaper({ title, children }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 2,
                borderColor: "primary.main",
                borderRadius: 1,
            }}
        >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {title}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {children}
        </Paper>
    );
}

export default function RFTestReportViewDialog({ open, onClose, report }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const cropFloorPlanViewBox = useCallback((node) => {
        if (!node) return;
        const svgEl = node.querySelector("svg");
        if (!svgEl) return;
        try {
            const bbox = svgEl.getBBox();
            if (bbox.width && bbox.height) {
                const pad = Math.max(bbox.width, bbox.height) * 0.05;
                svgEl.setAttribute(
                    "viewBox",
                    `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${
                        bbox.height + pad * 2
                    }`,
                );
            }
        } catch {
            // SVG not measurable — leave the original viewBox in place
        }
    }, []);

    if (!report) return null;

    const {
        report_number,
        project_detail,
        quote_detail,
        created_by_detail,
        test_type,
        location,
        magnet_type,
        magnet_strength,
        witness_name,
        witness_company,
        target_attenuation,
        test_points,
        frequencies,
        scores,
        notes,
        floor_plan,
        created,
    } = report;

    const renderFloorPlan = () => {
        if (!floor_plan) return null;
        let fpData = floor_plan;
        if (typeof floor_plan === "string") {
            try {
                fpData = JSON.parse(floor_plan);
            } catch {
                return null;
            }
        }
        if (!fpData?.svg) return null;
        // Strip full-canvas white background rect
        const svgContent = fpData.svg.replace(
            /<rect\s+width="100%"\s+height="100%"\s+fill="white"\s*\/>/g,
            "",
        );
        return (
            <Box
                sx={{
                    width: "100%",
                    height: { xs: 400, sm: 600 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "#ffffff",
                    overflow: "hidden",
                }}
            >
                <Box
                    key={report_number}
                    ref={cropFloorPlanViewBox}
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        "& svg": {
                            width: "100%",
                            height: "100%",
                            display: "block",
                        },
                    }}
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                />
            </Box>
        );
    };

    const linkedLabel = project_detail
        ? `${project_detail.number} — ${project_detail.name}`
        : quote_detail
          ? `${quote_detail.number} — ${quote_detail.name}`
          : "—";

    const createdByName = created_by_detail
        ? created_by_detail.full_name || created_by_detail.username
        : "—";

    const magnetDisplay = formatGEMagnetDisplay(magnet_type, magnet_strength);
    const targetAttenuationDisplay =
        target_attenuation !== null && target_attenuation !== undefined
            ? `${target_attenuation} dB`
            : "100 dB";

    const addressDetail =
        project_detail?.address_detail || quote_detail?.address_detail || null;
    const addressLine = addressDetail
        ? [
              addressDetail.address,
              addressDetail.city,
              addressDetail.state,
              addressDetail.postal_code,
          ]
              .filter(Boolean)
              .join(", ")
        : null;

    // Build grouped score rows: group by frequency index
    const scoreRows = [];
    if (
        frequencies &&
        frequencies.length &&
        test_points &&
        test_points.length
    ) {
        frequencies.forEach((freq, fi) => {
            test_points.forEach((tp) => {
                const key = `${fi}-${tp}`;
                const score = (scores || {})[key] || {};
                scoreRows.push({
                    freq,
                    fi,
                    tp,
                    testLevel: score.testLevel,
                    shieldingEffectiveness: score.shieldingEffectiveness,
                });
            });
        });
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle sx={{ position: "relative", pr: 6 }}>
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Typography variant="h6">
                        RF Test Report — {report_number}
                    </Typography>
                    <Chip
                        label={`Target: ${targetAttenuationDisplay}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Report Information */}
                <SectionPaper title="Report Information">
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Report #" value={report_number} />
                            <InfoRow label="Location" value={location} />
                            <InfoRow
                                label="Test Date"
                                value={formatDate(created)}
                            />
                            <InfoRow label="Created By" value={createdByName} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InfoRow label="Test Type" value={test_type} />
                            <InfoRow label="Magnet" value={magnetDisplay} />
                            <InfoRow
                                label="Target Attenuation"
                                value={targetAttenuationDisplay}
                            />
                        </Grid>
                    </Grid>
                </SectionPaper>

                {/* Project / Quote */}
                <SectionPaper title="Project Information">
                    <InfoRow
                        label={project_detail ? "Project" : "Quote"}
                        value={linkedLabel}
                    />
                    {addressLine && (
                        <InfoRow label="Address" value={addressLine} />
                    )}
                </SectionPaper>

                {/* Witness */}
                {(witness_name || witness_company) && (
                    <SectionPaper title="Witness Information">
                        <InfoRow label="Name" value={witness_name} />
                        <InfoRow label="Company" value={witness_company} />
                    </SectionPaper>
                )}

                {/* Floor Plan */}
                {floor_plan && (
                    <SectionPaper title="Floor Plan">
                        {renderFloorPlan()}
                    </SectionPaper>
                )}

                {/* Test Frequencies */}
                {frequencies && frequencies.length > 0 && (
                    <SectionPaper title="Frequencies">
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {frequencies.map((freq, idx) => (
                                <Chip
                                    key={idx}
                                    label={`${freq.frequency} ${freq.unit}${
                                        freq.reference
                                            ? ` (Ref: ${freq.reference} dBμV)`
                                            : ""
                                    }`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </SectionPaper>
                )}

                {/* Test Points */}
                {test_points && test_points.length > 0 && (
                    <SectionPaper title="Test Points">
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {test_points.map((tp) => (
                                <Chip key={tp} label={tp} size="small" />
                            ))}
                        </Box>
                    </SectionPaper>
                )}

                {/* Test Results */}
                {scoreRows.length > 0 && (
                    <SectionPaper title="Test Results">
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Frequency
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Test Point
                                        </TableCell>
                                        <TableCell
                                            sx={{ fontWeight: 700 }}
                                            align="center"
                                        >
                                            Test Level (dBμV)
                                        </TableCell>
                                        <TableCell
                                            sx={{ fontWeight: 700 }}
                                            align="center"
                                        >
                                            Shielding Effectiveness (dB)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scoreRows.map((row, idx) => {
                                        const showFreq =
                                            idx === 0 ||
                                            scoreRows[idx - 1].fi !== row.fi;
                                        const rowspan = test_points
                                            ? test_points.length
                                            : 1;
                                        return (
                                            <TableRow
                                                key={`${row.fi}-${row.tp}`}
                                            >
                                                {showFreq && (
                                                    <TableCell
                                                        rowSpan={rowspan}
                                                        sx={{
                                                            verticalAlign:
                                                                "top",
                                                            pt: 1.5,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={500}
                                                        >
                                                            {row.freq.frequency}{" "}
                                                            {row.freq.unit}
                                                        </Typography>
                                                        {row.freq.reference && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Ref:{" "}
                                                                {
                                                                    row.freq
                                                                        .reference
                                                                }{" "}
                                                                dBμV
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {row.tp}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">
                                                        {row.testLevel || "—"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.shieldingEffectiveness ? (
                                                        (() => {
                                                            const chip =
                                                                getScoreChipStyle(
                                                                    row.shieldingEffectiveness,
                                                                    target_attenuation ??
                                                                        100,
                                                                );
                                                            return (
                                                                <Chip
                                                                    label={`${row.shieldingEffectiveness} dB`}
                                                                    size="small"
                                                                    color={
                                                                        chip.color
                                                                    }
                                                                    sx={chip.sx}
                                                                />
                                                            );
                                                        })()
                                                    ) : (
                                                        <Typography variant="body2">
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionPaper>
                )}

                {/* Notes */}
                {notes && notes.trim() && (
                    <SectionPaper title="Notes">
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            {notes}
                        </Typography>
                    </SectionPaper>
                )}
            </DialogContent>

            <DialogActions
                sx={
                    fullScreen
                        ? { pb: "max(8px, env(safe-area-inset-bottom))" }
                        : undefined
                }
            >
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
